import { FormEvent, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Tooltip } from 'primereact/tooltip'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Dialog } from 'primereact/dialog'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

// local imports & assets
import confirmImage from 'assets/images/warning-image.svg'
import carGray from 'assets/icons/car-gray.svg'
import parkGray from 'assets/icons/park-area-gray.svg'
import DateUtil from 'utils/DateUtil'
import { licenceRegex, toInputUppercase } from 'utils/Miscellaneous'
import { IParkAreaReservation } from 'types/ParkAreaType'
import ParkAreaService from 'service/ParkAreaService'

type Mode = 'readonly' | 'edit'

interface IParkReservationProps {
  serviceIsExists: boolean | undefined
  resDate: Date
  parkRes: IParkAreaReservation
  refreshCalendar: () => void
}

const today = new Date()

export default function ParkAreaEdit({ resDate, parkRes, refreshCalendar, serviceIsExists }: IParkReservationProps) {
  const { t } = useTranslation()
  const history = useHistory()
  const [plate, setPlate] = useState(parkRes?.plate)
  const [valid, setValid] = useState(true)
  const [mode, setMode] = useState<Mode>('readonly')
  const [deleteDialog, setDeleteDialog] = useState(false)
  const [recentlyDeleted, setRecentlyDeleted] = useState(false)

  const setEditMode = () => setMode('edit')
  const setReadOnlyMode = () => {
    setMode('readonly')
    setPlate(parkRes?.plate)
    setValid(true)
  }

  const handleInputChange = (e: FormEvent<HTMLInputElement>) => {
    const isValid = licenceRegex.test(e.currentTarget.value)
    setValid(isValid)
    setPlate(e.currentTarget.value)
  }

  const handleUpdate = () => {
    const body = {
      id: parkRes?.id,
      plate: plate,
      reservationStartTime: DateUtil.formatStringToDateTime(parkRes?.reservationStartHour),
      reservationEndTime: DateUtil.formatStringToDateTime(parkRes?.reservationEndHour),
      parkAreaId: parkRes?.parkAreaId,
    }

    ParkAreaService.saveParkAreaReservation(body)
      .then(() => {
        setMode('readonly')
        refreshCalendar()
      })
      .catch((err) => console.log(err))
      .finally(() => setValid(true))
  }

  const handleDeleteReservation = () => {
    ParkAreaService.deleteParkAreaReservation({ id: parkRes?.id, parkAreaId: parkRes?.parkAreaId })
      .then(() => {
        setMode('readonly')
        refreshCalendar()
        setRecentlyDeleted(true)
      })
      .catch((err) => console.log(err))
  }

  const redirectParkRes = () => history.push({ pathname: '/home', search: 'tab=park', state: { date: resDate } })
  const isTodayOrAfter = DateUtil.IsAfterDate(resDate, today)

  if (!parkRes || recentlyDeleted) {
    return isTodayOrAfter && !!serviceIsExists ? (
      <RedirectButton onClick={redirectParkRes} label={t('plantype.make-park-res')} />
    ) : null
  }

  const deleteDialogUI = () => {
    return (
      <Dialog
        modal
        header=""
        visible={deleteDialog}
        position={'center'}
        style={{ width: '400px' }}
        footer={renderDeleteDialogFooter()}
        onHide={() => setDeleteDialog(false)}
        draggable={false}
        resizable={false}>
        <div className="d-flex flex-column of-newplan-confirm-dialog">
          <div className="of-confirm-image">
            <img src={confirmImage} alt="" />
          </div>
          <div className="">
            <p className="of-confirm-info mt-2">{t('overlaypanel-texts.cancel-process')}</p>
          </div>
          <div className="of-confirm-detail">
            <p>{t('sidebar.cancel-park-area')}</p>
            <p>{t('sidebar.do-you-accept')}</p>
          </div>
        </div>
      </Dialog>
    )
  }

  /* Delete Reservation Dialog Footer Components  */
  const renderDeleteDialogFooter = () => {
    return (
      <div className="d-flex flex-column gap-3 of-confirm-footer">
        <div className="d-flex justify-content-between gap-3">
          <Button
            label={t('buttons.no')}
            onClick={() => setDeleteDialog(false)}
            style={{ width: '165px' }}
            className="p-button-outlined"
          />
          <Button
            label={t('buttons.yes')}
            onClick={() => {
              handleDeleteReservation()
              setDeleteDialog(false)
            }}
            style={{ width: '165px' }}
            className="of-button-primary"
          />
        </div>
      </div>
    )
  }

  return (
    <div className="d-flex flex-column gap-1">
      <div className="d-flex align-items-center gap-2">
        <div className="d-flex align-items-center">
          <img src={parkGray} width={24} height={24} alt="plate_number" />
          <span className="mx-1 info-date">{t('tabs.park-area')}: </span>
          {parkRes?.parkAreaName}
        </div>

        {isTodayOrAfter && !!serviceIsExists ? (
          <InfoCardTextButton className="p-button-text" width={160} height={30} onClick={() => setDeleteDialog(true)}>
            {t('Rezervasyonu İptal Et')}
          </InfoCardTextButton>
        ) : null}
      </div>

      <div className="d-flex align-items-center gap-1">
        <img src={carGray} width={24} height={20} alt="plate_number" />

        {mode === 'readonly' ? (
          <div className="d-flex align-items-center gap-2">
            <span className="mx-1 info-date">{t('park-area-res-modal.licence-plate')}: </span>
            <div>{plate}</div>

            {isTodayOrAfter && !!serviceIsExists ? (
              <InfoCardTextButton className="p-button-text" width={60} height={30} onClick={setEditMode}>
                {t('buttons.edit')}
              </InfoCardTextButton>
            ) : null}
          </div>
        ) : (
          <div className="d-flex align-items-center gap-2">
            <PlateInput
              name="plate"
              keyfilter="alphanum"
              value={plate}
              onChange={handleInputChange}
              onInput={toInputUppercase}
              className={valid ? '' : 'p-invalid'}
              maxLength={8}
              spellCheck={false}
              autoFocus
            />

            {!valid ? (
              <>
                <i id="warning-tooltip" className="pi pi-info-circle text-danger" />
                <Tooltip
                  target="#warning-tooltip"
                  position="bottom"
                  content={t('park-area-res-modal.valid-plate-msg')}
                />
              </>
            ) : null}

            <InfoCardTextButton
              disabled={!valid}
              className="p-button-text"
              width={65}
              height={25}
              onClick={handleUpdate}>
              {t('buttons.save')}
            </InfoCardTextButton>

            <InfoCardTextButton
              className="p-button-text p-button-secondary"
              width={65}
              height={25}
              onClick={setReadOnlyMode}>
              {t('buttons.cancel')}
            </InfoCardTextButton>
          </div>
        )}
      </div>

      {deleteDialogUI()}
    </div>
  )
}

/* Styled Components */
const RedirectButton = styled(Button)`
  width: 300px;
  text-align: center;
  border-radius: 10px;
  background-color: #45bfe5;
`
const InfoCardTextButton = styled(Button)`
  width: ${({ width }: { width?: number; height?: number }) => `${width || 100}px`};
  height: ${({ height }: { height?: number }) => `${height || 40}px`};
  justify-content: center;
  text-decoration: underline;
  border-radius: 10px;
  margin: 0;
  padding: 0;

  :hover {
    background-color: transparent !important;
  }

  :focus {
    outline: 0 none;
    outline-offset: 0;
    box-shadow: none;
    background-color: none;
  }

  :disabled {
    opacity: 0.4;
  }
`
const PlateInput = styled(InputText)`
  width: 120px;
  height: 30px;
  border-radius: 5px;
  font-size: 15px;
  line-height: 16px;
  font-family: 'Inter', serif !important;
  font-weight: 500;
  text-align: center;
  margin-right: 4px;
`
